import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { useParams, useSearchParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import { ApiError } from "../../api/errors";
import type { CampaignDetail, ConfigVersionView, CreativeVersionView, CurrentTerms, PromoteReleaseResponse } from "../../api/types";
import { DomainVerification } from "../../components/DomainVerification";
import { CommandError } from "../../components/CommandError";
import { Banner, Button, Card, ErrorState, Money, PageHeader, SkeletonBlock, StatusPill } from "../../components/ui";
import { formatLocal, formatUtc } from "../../lib/datetime";
import { useIntentKey } from "../../lib/useIntentKey";
import { ReviseCard, VersionLifecycleRows, WithdrawControl } from "./CampaignCandidate";
import { CampaignReports } from "./CampaignReports";
import { BudgetCard, ControlsCard, NameCard, ScheduleCard } from "./CampaignSettings";
import { campaignName } from "./Campaigns";
import { CAMPAIGN_STATE, CampaignStatePills, formatRate, resumeBlockedMessage, ReviewPill } from "./campaignCopy";

// UX-DESIGN.md §2.4 (status, versions, terms, release) and §2.5 (reporting),
// all from GET /v1/campaigns/{id} (G-13, campaign_read.go:101 →
// campaign_detail, 0052 + 0054). The page never infers a state: it shows
// every state the server derived, and the live and pending-review versions
// as separate rows.

export function CampaignDetailPage() {
  const { campaignId = "" } = useParams();
  const [params] = useSearchParams();
  const ep = useEndpoints();
  const q = useQuery({ queryKey: ["campaign", campaignId], queryFn: () => ep.advertiser.getCampaign(campaignId) });
  const requireDomainControl = useAdvertiserDomainControlRequired();
  const justSubmitted = params.get("submitted") === "1";
  const c = q.data;

  return (
    <div className="stack-lg">
      <PageHeader
        title={c ? campaignName(c) : "Campaign"}
        back={{ to: "/advertiser/campaigns", label: "Campaigns" }}
        description={
          <>
            ID <code>{campaignId}</code>
          </>
        }
      />
      {justSubmitted ? (
        <Banner tone="success" title="Submitted for review" live="polite">
          The campaign, its draft config and creative versions, and your terms acceptance were all recorded.{" "}
          {requireDomainControl
            ? "Next: prove you control the destination domain below — review can't approve the creative until you do."
            : "It's now in the review queue."}
        </Banner>
      ) : null}

      {q.isPending ? <SkeletonBlock label="Loading campaign" lines={6} /> : null}
      {q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : null}
      {c ? (
        <>
          <StatusCard c={c} />
          <NameCard c={c} />
          <VersionsCard c={c} />
          <ReviseCard c={c} />
          <TermsCard c={c} />
          <ReleaseCard c={c} />
          <DestinationCard c={c} requireDomainControl={requireDomainControl} />
          <ScheduleCard c={c} />
          <BudgetCard c={c} />
          <PacingCard c={c} />
          <ControlsCard c={c} />
          <CampaignReports c={c} />
        </>
      ) : null}
    </div>
  );
}

function StatusCard({ c }: { c: CampaignDetail }) {
  return (
    <Card title="Status" subtitle="Every state that applies is shown. Live and pending-review versions are always shown separately.">
      <div className="stack">
        <CampaignStatePills states={c.states} />
        {c.states.map((s) => {
          const copy = CAMPAIGN_STATE[s];
          if (!copy) return null;
          let extra: ReactNode = null;
          if (s === "paused" && c.eligibility.resume_blocker) extra = <strong>{resumeBlockedMessage(c.eligibility.resume_blocker)}</strong>;
          if (s === "budget_exhausted")
            extra = c.budget.today?.exhausted && !c.budget.lifetime?.exhausted ? "Today's daily budget is used up." : null;
          return (
            <Banner key={s} tone={copy.tone} title={copy.label}>
              <div className="stack-sm">
                <span>{copy.detail}</span>
                {extra ? <span>{extra}</span> : null}
              </div>
            </Banner>
          );
        })}
        {c.budget.legacy_unbounded ? (
          <Banner tone="warning" title="No budget cap">
            This campaign went live before budgets existed, so its spend is capped only by your wallet balance. Set a budget below.
          </Banner>
        ) : null}
      </div>
    </Card>
  );
}

function ConfigSummary({ c, v }: { c: CampaignDetail; v: ConfigVersionView }) {
  const per = v.pricing_model === "cpm" ? "per 1,000 impressions" : "per engagement";
  return (
    <dl className="definition-list">
      <dt>Targeting &amp; bid</dt>
      <dd>
        Version {v.version_no} <ReviewPill status={v.status} />
      </dd>
      <dt>Bid</dt>
      <dd>
        <Money value={v.bid_amount} currency={v.currency} /> {per} ({v.pricing_model.toUpperCase()})
      </dd>
      <dt>Lifetime budget</dt>
      <dd>{v.lifetime_budget ? <Money value={v.lifetime_budget} currency={v.currency} /> : "None (created before budgets existed)"}</dd>
      <dt>Daily budget</dt>
      <dd>{v.daily_budget ? <Money value={v.daily_budget} currency={v.currency} /> : "No daily cap"}</dd>
      <dt>Budget time zone</dt>
      <dd>{v.budget_timezone}</dd>
      <dt>Frequency cap</dt>
      <dd>
        {v.frequency_cap
          ? `${v.frequency_cap.max_impressions} impressions per session, every ${v.frequency_cap.window_hours} hours`
          : "None"}
      </dd>
      {v.rejection ? (
        <>
          <dt>Rejected</dt>
          <dd>
            <RejectionNote message={v.rejection.message} at={v.rejection.rejected_at} />
          </dd>
        </>
      ) : null}
      <VersionLifecycleRows c={c} kind="config" v={v} />
    </dl>
  );
}

function CreativeSummary({ c, v }: { c: CampaignDetail; v: CreativeVersionView }) {
  return (
    <dl className="definition-list">
      <dt>Creative</dt>
      <dd>
        Version {v.version_no} <ReviewPill status={v.status} />
      </dd>
      <dt>Title</dt>
      <dd>{v.title}</dd>
      <dt>Body</dt>
      <dd>{v.body}</dd>
      <dt>Destination</dt>
      <dd className="break-all">
        {v.destination_url}{" "}
        {v.destination.domain_verified_at ? (
          <StatusPill tone="success">Domain verified</StatusPill>
        ) : (
          <StatusPill tone="warning">Domain not verified</StatusPill>
        )}{" "}
        {v.destination.suspended ? <StatusPill tone="error">Destination suspended</StatusPill> : null}
      </dd>
      {v.rejection ? (
        <>
          <dt>Rejected</dt>
          <dd>
            <RejectionNote message={v.rejection.message} at={v.rejection.rejected_at} />
          </dd>
        </>
      ) : null}
      <VersionLifecycleRows c={c} kind="creative" v={v} />
    </dl>
  );
}

function RejectionNote({ message, at }: { message: string; at: string }) {
  return (
    <span className="stack-sm">
      <span>{message}</span>
      <span className="xs muted">
        {formatLocal(at)}. What's live (if anything) is unchanged. Revise it below, or appeal the decision once.
      </span>
    </span>
  );
}

function VersionsCard({ c }: { c: CampaignDetail }) {
  const p = c.pending;
  return (
    <Card title="Versions" subtitle="What's live keeps serving exactly as it is until a pending version is approved and promoted.">
      <div className="stack">
        <section className="stack-sm" aria-label="Live">
          <h3>Live</h3>
          {c.live ? (
            <div className="grid-2">
              <ConfigSummary c={c} v={c.live.config_version} />
              <CreativeSummary c={c} v={c.live.creative_version} />
            </div>
          ) : (
            <p className="muted">Nothing is live yet.</p>
          )}
        </section>
        <section className="stack-sm" aria-label="Pending review">
          <h3>Pending review</h3>
          {p ? (
            <>
              {c.live ? <p className="small">This will replace the live version once approved and promoted.</p> : null}
              {!p.submitted ? (
                <Banner tone="warning" title="Not submitted yet">
                  These versions exist, but no terms acceptance is bound to them, so review hasn't started. Accept the terms below.
                </Banner>
              ) : (
                <p className="small muted">Submitted {p.submitted_at ? formatLocal(p.submitted_at) : ""}.</p>
              )}
              {p.schedule_changed_since_submission && p.submitted_schedule ? (
                <Banner tone="warning" title="Schedule changed since this review was submitted">
                  Submitted with {formatUtc(p.submitted_schedule.schedule_start_utc)} →{" "}
                  {p.submitted_schedule.schedule_end_utc ? formatUtc(p.submitted_schedule.schedule_end_utc) : "no end date"}; the
                  campaign's schedule is now {formatUtc(c.schedule_start_utc)} →{" "}
                  {c.schedule_end_utc ? formatUtc(c.schedule_end_utc) : "no end date"}.
                </Banner>
              ) : null}
              <div className="grid-2">
                {p.config_version ? <ConfigSummary c={c} v={p.config_version} /> : <p className="small muted">Targeting &amp; bid: unchanged from live.</p>}
                {p.creative_version ? <CreativeSummary c={c} v={p.creative_version} /> : <p className="small muted">Creative: unchanged from live.</p>}
              </div>
            </>
          ) : (
            <p className="muted">No version is pending review.</p>
          )}
          <WithdrawControl c={c} />
        </section>
      </div>
    </Card>
  );
}

function candidateCurrency(c: CampaignDetail): string {
  return c.pending?.config_version?.currency ?? c.live?.config_version.currency ?? c.budget.currency ?? "USD";
}

function TermsCard({ c }: { c: CampaignDetail }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const intent = useIntentKey();
  const currency = candidateCurrency(c);
  const pair = c.pending_candidate;
  const needsAcceptance =
    !!c.pending && !!pair?.config_version_id && !!pair.creative_version_id && (!c.pending.submitted || c.terms.reacceptance_required);
  const terms = useQuery({
    queryKey: ["commercial-terms", currency],
    queryFn: () => ep.advertiser.getCurrentTerms(currency),
    enabled: needsAcceptance,
  });
  const accept = useMutation({
    mutationFn: (t: CurrentTerms) => {
      const body = {
        platform_terms_version_id: t.platform_terms_version_id,
        config_version_id: pair!.config_version_id!,
        creative_version_id: pair!.creative_version_id!,
      };
      return ep.advertiser.acceptTerms(c.campaign_id, body, { idempotencyKey: intent.keyFor(JSON.stringify(body)) });
    },
    onSuccess: () => {
      intent.reset();
      void qc.invalidateQueries({ queryKey: ["campaign", c.campaign_id] });
      void qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "terms_version_changed") {
        const fresh = err.detail?.current_terms as CurrentTerms | undefined;
        if (fresh?.platform_terms_version_id) qc.setQueryData(["commercial-terms", currency], fresh);
        else void terms.refetch();
      }
    },
  });

  const live = c.terms.live_acceptance;
  const pending = c.terms.pending_acceptance;
  return (
    <Card title="Commercial terms" subtitle="Acceptance is pinned to a terms version and to the exact config and creative versions.">
      <div className="stack">
        <dl className="definition-list">
          <dt>Live release</dt>
          <dd>
            {live ? (
              <>
                Platform fee {formatRate(live.platform_fee_rate)}, accepted {formatLocal(live.accepted_at)}
              </>
            ) : (
              "—"
            )}
          </dd>
          <dt>Pending versions</dt>
          <dd>
            {pending ? (
              <>
                Platform fee {formatRate(pending.platform_fee_rate)}, accepted {formatLocal(pending.accepted_at)}
              </>
            ) : c.pending ? (
              "Not accepted yet"
            ) : (
              "—"
            )}
          </dd>
        </dl>
        {c.terms.reacceptance_required ? (
          <Banner tone="warning" title="Terms changed — re-accept to activate">
            The commercial terms changed after you accepted them for the pending versions. The release can't be promoted until you accept
            the current terms.
          </Banner>
        ) : null}
        {needsAcceptance ? (
          terms.isPending ? (
            <SkeletonBlock label="Loading the current commercial terms" lines={2} />
          ) : terms.isError ? (
            <ErrorState error={terms.error} onRetry={() => void terms.refetch()} />
          ) : (
            <div className="stack-sm">
              <p>
                Current terms: platform fee <strong>{formatRate(terms.data.platform_fee_rate)}</strong>, publisher share{" "}
                {formatRate(terms.data.publisher_share_rate)}, {terms.data.currency}, rounding <code>{terms.data.rounding_rule}</code>{" "}
                <span className="xs muted">(version <code>{terms.data.platform_terms_version_id}</code>)</span>
              </p>
              {accept.isError ? <CommandError error={accept.error} /> : null}
              <div>
                <Button onClick={() => accept.mutate(terms.data)} loading={accept.isPending}>
                  Accept these terms for the pending versions
                </Button>
              </div>
            </div>
          )
        ) : null}
      </div>
    </Card>
  );
}

function ReleaseCard({ c }: { c: CampaignDetail }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const intent = useIntentKey();
  const pair = c.pending_candidate;
  const [result, setResult] = useState<PromoteReleaseResponse | null>(null);
  const promote = useMutation({
    mutationFn: () => {
      const body = { config_version_id: pair!.config_version_id!, creative_version_id: pair!.creative_version_id! };
      return ep.advertiser.promoteRelease(c.campaign_id, body, { idempotencyKey: intent.keyFor(JSON.stringify(body)) });
    },
    onSuccess: (res) => {
      intent.reset();
      setResult(res);
      void qc.invalidateQueries({ queryKey: ["campaign", c.campaign_id] });
      void qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });
  const ready = c.states.includes("approved_ready_to_activate");
  const ended = c.states.includes("ended");
  const err = promote.error instanceof ApiError ? promote.error : null;
  const stillInReview = err?.code === "config_version_not_approved" || err?.code === "creative_version_not_approved";

  return (
    <Card title="Release" subtitle="Once both pending versions are approved in review, promote them together as the campaign's live release.">
      <div className="stack">
        {result ? (
          <Banner tone="success" live="polite" title={result.replayed ? "Already promoted" : "Release promoted"}>
            Release <code>{result.release_version_id}</code> is now the campaign's active release. Whether it's serving right now also
            depends on the schedule, pause state, budget and wallet balance.
          </Banner>
        ) : null}
        {!pair?.config_version_id || !pair.creative_version_id || ended ? (
          <p className="muted">{ended ? "This campaign has ended." : "There's nothing pending to promote."}</p>
        ) : (
          <>
            <p className="small">
              {ready
                ? "Both versions are approved. Promoting makes them live; any new lifetime and daily budget on the config version takes effect now."
                : "Promotion is refused until review approves both versions and the current terms are accepted for them."}
            </p>
            {stillInReview ? (
              <Banner tone="info" live="polite" title="Still in review">
                {err!.message} Try again after review completes — you'll need to check back here until notifications exist (API-GAPS.md
                G-08).
              </Banner>
            ) : promote.isError ? (
              <CommandError error={promote.error} />
            ) : null}
            <div>
              <Button variant={ready ? "primary" : "secondary"} onClick={() => promote.mutate()} loading={promote.isPending}>
                Promote release
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * This UTC hour's impression pacing (migration 0069, campaign_detail's
 * `pacing`: hour_start_utc/quota/outstanding/consumed/remaining, all plain
 * impression counts — never money). A fresh hour with no reservation
 * attempts yet reads at the full quota, 0 used, 0 in flight; that's not
 * a loading state, so it's shown exactly as reported.
 */
function PacingCard({ c }: { c: CampaignDetail }) {
  const p = c.pacing;
  const used = p.quota > 0 ? Math.min(100, Math.round(((p.quota - p.remaining) / p.quota) * 100)) : 0;
  const tone = used >= 100 ? "error" : used >= 80 ? "warning" : "primary";
  return (
    <Card
      title="This hour's pacing"
      subtitle={`Hour starting ${formatUtc(p.hour_start_utc)} (UTC). Impression counts, not money; resets every hour.`}
    >
      <div className="stack">
        <div className="grid-auto">
          <div className="stat">
            <span className="stat-label">Quota</span>
            <span className="stat-value">{p.quota.toLocaleString("en-US")}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Used</span>
            <span className="stat-value">{p.consumed.toLocaleString("en-US")}</span>
          </div>
          <div className="stat">
            <span className="stat-label">In flight</span>
            <span className="stat-value">{p.outstanding.toLocaleString("en-US")}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Remaining</span>
            <span className="stat-value">{p.remaining.toLocaleString("en-US")}</span>
          </div>
        </div>
        <div
          className="meter"
          role="img"
          aria-label={`${used}% of this hour's quota used or reserved: ${p.consumed.toLocaleString("en-US")} used, ${p.outstanding.toLocaleString("en-US")} in flight, of ${p.quota.toLocaleString("en-US")}.`}
        >
          <span className={`meter-fill meter-${tone}`} style={{ width: `${used}%` }} />
        </div>
        <p className="xs muted">
          Used is delivered this hour; in flight is reserved but not yet delivered or released. Remaining is what's left in this
          hour's quota (never negative).
        </p>
      </div>
    </Card>
  );
}

// useAdvertiserDomainControlRequired reads advertiser_destination_policy
// (migration 0078, owner decision 2026-09-27) through GET /v1/environment
// — the same query EnvironmentIndicator.tsx already makes (identical
// queryKey, so React Query serves both from one fetch). Defaults to
// false while loading or on error: the fail-safe direction here is
// "don't show a step that isn't required," never the reverse.
function useAdvertiserDomainControlRequired(): boolean {
  const ep = useEndpoints();
  const q = useQuery({
    queryKey: ["environment"],
    queryFn: () => ep.system.environment(),
    staleTime: Infinity,
    retry: false,
  });
  return q.data?.advertiser_domain_control_required ?? false;
}

// MIGRATION 0078: advertisers are no longer required to prove domain
// ownership of a creative's destination (default policy: off). When the
// policy is off, this card drops the DomainVerification step entirely —
// there is nothing to prove, and showing a step nobody has to complete
// would misdescribe it as required. Publisher site verification
// (SiteDetail.tsx) is a different screen entirely and is unaffected.
function DestinationCard({ c, requireDomainControl }: { c: CampaignDetail; requireDomainControl: boolean }) {
  const ep = useEndpoints();
  const creativeId = c.pending?.creative_version_id ?? c.live?.creative_version.creative_version_id ?? null;
  const verifiedAlready =
    c.pending?.creative_version?.destination.domain_verified_at != null ||
    c.live?.creative_version.destination.domain_verified_at != null;

  if (!requireDomainControl) {
    return (
      <Card title="Destination domain" subtitle="Optional. You are not required to prove you control this domain.">
        <p className="small muted">
          {verifiedAlready
            ? "This destination's domain control has been verified. That is shown to staff review as optional information, not required for approval."
            : "Your destination can be any https URL, including a page you don't control the domain of (a social profile, a marketplace listing, an app store page, and similar). Staff review still checks the link itself."}
        </p>
      </Card>
    );
  }

  return (
    <Card
      title="Destination domain"
      subtitle="Prove you control the creative's destination. Review can't approve the creative until this passes; one proof covers every creative pointing at the same host."
    >
      {creativeId ? (
        <DomainVerification
          subject="destination"
          queryKey={["creative-domain", c.campaign_id, creativeId]}
          load={() => ep.advertiser.getCreativeDomainChallenge(c.campaign_id, creativeId)}
          verify={() => ep.advertiser.verifyCreativeDomain(c.campaign_id, creativeId)}
        />
      ) : (
        <p className="small muted">This campaign has no creative version yet.</p>
      )}
      <p className="xs muted" style={{ marginTop: "var(--space-3)" }}>
        Passing this check means the domain is proven — full destination review (content and safety scanning) still runs separately.
      </p>
    </Card>
  );
}
